import type {
	AudioTrack,
	EffectTrack,
	GraphicTrack,
	OverlayTrack,
	SceneTracks,
	TextTrack,
	TimelineElement,
	VideoTrack,
} from "@/timeline";
import { generateUUID } from "@/utils/id";
import { buildEmptyTrack } from "./track-factory";
import type { PlacementResult } from "./types";
import { updateTrackInSceneTracks } from "@/timeline/track-element-update";

export function applyPlacement({
	tracks,
	placementResult,
	elements,
	newTrackInsertIndexOverride,
}: {
	tracks: SceneTracks;
	placementResult: PlacementResult;
	elements: TimelineElement[];
	newTrackInsertIndexOverride?: number;
}): { updatedTracks: SceneTracks; targetTrackId: string } | null {
	const orderedTracks = [...tracks.overlay, tracks.main, ...tracks.audio];
	if (placementResult.kind === "existingTrack") {
		const targetTrack = orderedTracks[placementResult.trackIndex];
		if (!targetTrack) {
			return null;
		}

		const updatedTracks = updateTrackInSceneTracks({
			tracks,
			trackId: targetTrack.id,
			update: (track) => ({
				...track,
				elements: [...track.elements, ...elements],
			}),
		});

		return { updatedTracks, targetTrackId: targetTrack.id };
	}

	const newTrackId = generateUUID();
	const insertIndex =
		newTrackInsertIndexOverride ?? placementResult.insertIndex;
	const updatedTracks =
		placementResult.trackType === "audio"
			? {
					...tracks,
					audio: insertIntoAudioTracks({
						tracks,
						insertIndex,
						track: buildPlacedAudioTrack({
							id: newTrackId,
							elements,
						}),
					}),
				}
			: {
					...tracks,
					overlay: insertIntoOverlayTracks({
						tracks,
						insertIndex,
						track: buildPlacedOverlayTrack({
							id: newTrackId,
							type: placementResult.trackType,
							elements,
						}),
					}),
				};
	return { updatedTracks, targetTrackId: newTrackId };
}

function insertIntoOverlayTracks({
	tracks,
	insertIndex,
	track,
}: {
	tracks: SceneTracks;
	insertIndex: number;
	track: OverlayTrack;
}): OverlayTrack[] {
	const normalizedInsertIndex = Math.max(
		0,
		Math.min(insertIndex, tracks.overlay.length),
	);
	const nextTracks = [...tracks.overlay];
	nextTracks.splice(normalizedInsertIndex, 0, track);
	return nextTracks;
}

function insertIntoAudioTracks({
	tracks,
	insertIndex,
	track,
}: {
	tracks: SceneTracks;
	insertIndex: number;
	track: AudioTrack;
}): AudioTrack[] {
	const audioInsertIndex = Math.max(
		0,
		Math.min(insertIndex - tracks.overlay.length - 1, tracks.audio.length),
	);
	const nextTracks = [...tracks.audio];
	nextTracks.splice(audioInsertIndex, 0, track);
	return nextTracks;
}

function buildPlacedAudioTrack({
	id,
	elements,
}: {
	id: string;
	elements: TimelineElement[];
}): AudioTrack {
	if (!elements.every((element): element is AudioTrack["elements"][number] => element.type === "audio")) {
		throw new Error("Audio track received a non-audio element");
	}
	return {
		...buildEmptyTrack({ id, type: "audio" }),
		elements,
	};
}

function buildPlacedOverlayTrack({
	id,
	type,
	elements,
}: {
	id: string;
	type: Exclude<OverlayTrack["type"], "audio">;
	elements: TimelineElement[];
}): OverlayTrack {
	switch (type) {
		case "video":
			if (!elements.every((element): element is VideoTrack["elements"][number] => element.type === "video" || element.type === "image")) {
				throw new Error("Video track received an incompatible element");
			}
			return {
				...buildEmptyTrack({ id, type: "video" }),
				elements,
			};
		case "text":
			if (!elements.every((element): element is TextTrack["elements"][number] => element.type === "text")) {
				throw new Error("Text track received an incompatible element");
			}
			return {
				...buildEmptyTrack({ id, type: "text" }),
				elements,
			};
		case "graphic":
			if (!elements.every((element): element is GraphicTrack["elements"][number] => element.type === "graphic" || element.type === "sticker")) {
				throw new Error("Graphic track received an incompatible element");
			}
			return {
				...buildEmptyTrack({ id, type: "graphic" }),
				elements,
			};
		case "effect":
			if (!elements.every((element): element is EffectTrack["elements"][number] => element.type === "effect")) {
				throw new Error("Effect track received an incompatible element");
			}
			return {
				...buildEmptyTrack({ id, type: "effect" }),
				elements,
			};
	}
}
